import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { QrCode, Send, Play, PhoneIncoming, ThumbsUp, ThumbsDown, Clock, Radar, Eye, RotateCw } from 'lucide-react';

const EVENT_META = {
  sent: { icon: Send, cls: 'text-slate-400' },
  scan: { icon: QrCode, cls: 'text-sky-400' },
  play_video: { icon: Play, cls: 'text-sky-400' },
  play_audio: { icon: Play, cls: 'text-sky-400' },
  lead: { icon: PhoneIncoming, cls: 'text-amber-400' },
  followup_interested: { icon: ThumbsUp, cls: 'text-emerald-400' },
  followup_no_interest: { icon: ThumbsDown, cls: 'text-red-400' },
  followup_later: { icon: Clock, cls: 'text-amber-400' },
};

const relTime = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const fmtDur = (secs) => {
  if (secs == null) return '—';
  const m = Math.floor(secs / 60);
  const sec = Math.round(secs % 60);
  return m > 0 ? `${m}:${String(sec).padStart(2, '0')} min` : `${sec}s`;
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

export const DealOutreachTracking = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [sessionsExpanded, setSessionsExpanded] = useState(false);

  const load = useCallback(() => {
    api.getDealOutreachTracking(projectId).then(r => setData(r.data)).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;

  const s = data.summary || {};
  const events = data.events || [];
  const shown = expanded ? events : events.slice(0, 6);

  const kpis = [
    { label: 'Letters sent', value: s.letters_sent || 0, icon: Send },
    { label: 'QR scans', value: s.scans || 0, icon: Eye, hot: (s.scans || 0) > 0 },
    { label: 'Media plays', value: s.plays || 0, icon: Play, hot: (s.plays || 0) > 0 },
    { label: 'Responses', value: (s.leads || 0) + (s.responded || 0) > 0 ? Math.max(s.leads || 0, s.responded || 0) : 0, icon: PhoneIncoming, hot: (s.leads || 0) + (s.responded || 0) > 0 },
  ];

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="deal-outreach-tracking">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <Radar className="w-4 h-4 text-sky-400" />
        <h3 className="font-heading text-base text-foreground">Outreach Tracking</h3>
        {s.last_scan_at && (
          <span className="text-[10px] text-muted-foreground ml-auto font-mono" data-testid="tracking-last-scan">
            last scan {relTime(s.last_scan_at)}
          </span>
        )}
      </div>
      {!data.has_outreach ? (
        <p className="text-xs text-muted-foreground p-4" data-testid="tracking-empty">
          No outreach yet for this deal — letters, QR scans, media plays and responses will show up here the moment they happen.
        </p>
      ) : (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {kpis.map(k => (
              <div key={k.label} className="bg-secondary/30 border border-border/60 rounded-sm px-3 py-2" data-testid={`tracking-kpi-${k.label.toLowerCase().replace(/ /g, '-')}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <k.icon className="w-3 h-3" />
                  <span className="text-[9px] uppercase tracking-wider">{k.label}</span>
                </div>
                <p className={`text-xl font-heading mt-0.5 ${k.hot ? 'text-sky-400' : 'text-foreground'}`}>{k.value}</p>
              </div>
            ))}
          </div>
          {s.response_days != null && (
            <p className="text-[11px] font-mono text-emerald-400 flex items-center gap-1.5" data-testid="tracking-response-time">
              <Clock className="w-3 h-3" />
              Owner responded {s.response_days < 1
                ? `${Math.max(1, Math.round(s.response_days * 24))}h`
                : `${Math.round(s.response_days * 10) / 10} days`} after the letter was sent
            </p>
          )}
          {data.engagement && data.engagement.sessions > 0 && (
            <div data-testid="tracking-engagement">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Engagement — how the owner interacted
              </p>
              <p className="text-[11px] font-mono text-foreground mb-1.5" data-testid="tracking-engagement-summary">
                ⌀ {fmtDur(data.engagement.avg_seconds_on_page)} on page
                {data.engagement.avg_scroll_pct != null ? ` · scrolled ⌀ ${data.engagement.avg_scroll_pct}%` : ''}
                {data.engagement.avg_video_pct != null ? ` · video watched ⌀ ${data.engagement.avg_video_pct}%` : ''}
                {data.engagement.avg_audio_pct != null ? ` · voice listened ⌀ ${data.engagement.avg_audio_pct}%` : ''}
                {data.engagement.avg_website_seconds != null ? ` · website read ⌀ ${fmtDur(data.engagement.avg_website_seconds)}` : ''}
              </p>
              <div className="space-y-1">
                {(data.sessions || []).slice(0, sessionsExpanded ? 20 : 4).map((sess, i) => (
                  <div key={i} className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5 flex-wrap" data-testid={`tracking-session-${i}`}>
                    <span className="text-muted-foreground/60">{fmtWhen(sess.started_at)}</span>
                    <span>·</span>
                    <span className="text-foreground">{fmtDur(sess.seconds_on_page)}</span>
                    {sess.location && <><span>·</span><span>📍 {sess.location}</span></>}
                    {sess.scroll_pct > 0 && <><span>·</span><span>scrolled {Math.round(sess.scroll_pct)}%</span></>}
                    {sess.video_pct > 0 && <><span>·</span><span className="text-sky-400">video {Math.round(sess.video_pct)}%</span></>}
                    {sess.audio_pct > 0 && <><span>·</span><span className="text-sky-400">voice {Math.round(sess.audio_pct)}%</span></>}
                    {sess.website_seconds > 0 && <><span>·</span><span className="text-emerald-400">website {fmtDur(sess.website_seconds)}{sess.website_pages > 1 ? ` (${sess.website_pages} pages)` : ''}</span></>}
                    {(sess.clicked_phone || sess.clicked_email || sess.clicked_calendar || sess.clicked_website) && (
                      <span className="text-emerald-400">
                        {[sess.clicked_phone && '📞', sess.clicked_email && '✉️', sess.clicked_calendar && '📅', sess.clicked_website && '🌐'].filter(Boolean).join(' ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {(data.sessions || []).length > 4 && (
                <button onClick={() => setSessionsExpanded(x => !x)} data-testid="tracking-sessions-toggle"
                  className="text-[11px] text-sky-400 hover:text-sky-300 transition-colors mt-1.5">
                  {sessionsExpanded ? 'Show less' : `Show all ${data.sessions.length} visits`}
                </button>
              )}
            </div>
          )}
          {events.length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2">Every touchpoint, newest first</p>
              <div className="space-y-1.5">
                {shown.map((e, i) => {
                  const meta = EVENT_META[e.type] || EVENT_META.scan;
                  return (
                    <div key={i} className="flex items-center gap-2.5 text-xs" data-testid={`tracking-event-${i}`}>
                      <meta.icon className={`w-3.5 h-3.5 shrink-0 ${meta.cls}`} />
                      <span className="text-foreground truncate">{e.detail}</span>
                      {e.type === 'lead' && e.lead_id && null}
                      {e.location && <span className="text-[10px] text-muted-foreground shrink-0">📍 {e.location}</span>}
                      <span className="ml-auto text-[10px] font-mono text-muted-foreground shrink-0" title={e.at}>{relTime(e.at)}</span>
                    </div>
                  );
                })}
              </div>
              {events.length > 6 && (
                <button onClick={() => setExpanded(x => !x)} data-testid="tracking-toggle-all"
                  className="text-[11px] text-sky-400 hover:text-sky-300 transition-colors mt-2">
                  {expanded ? 'Show less' : `Show all ${events.length} events`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
